/* eslint-disable react-refresh/only-export-components -- shared content module: exports both copy constants and the list component that renders them */
import { CalendarClock, Music, Users, Film, Aperture } from 'lucide-react';
import { Logo } from './Logo';

/**
 * Single source of truth for the app's marketing copy, shared between the auth
 * screen (`AuthPage`) and the Projects "About" section. Copy is taken from the
 * "Marketing & Vision → Current Features" section of README.md — no new
 * marketing copy invented.
 */
export const PROMO_HEADLINE = "It's your life, beautifully told, without the work";

export const APP_NAME = 'Life Movie Maker';

export const PROMO_TAGLINE =
  'Turn your camera roll into instant travel videos or an endless visual story.';

export const PROMO_POINTS = [
  { icon: CalendarClock, title: 'Automatic Video Processing', text: 'Upload your camera roll and videos are automatically sorted by date' },
  { icon: Aperture, title: 'Live / Motion Photo Extraction', text: 'Automatically extract the video from Live and Motion Photos' },
  { icon: Film, title: 'Continuous Timeline', text: 'One endless video that grows over time with new uploads' },
  { icon: Users, title: 'Collaborative Uploads', text: 'Invite friends to upload their camera roll and enjoy it magically sorting into the correct spot' },
  { icon: Music, title: 'Music Integration', text: 'Add music to bring you back to the moment' },
];

/**
 * The promo feature bullets, rendered as a list. Shared between the auth
 * screen and the Projects "About" section so the copy stays single-sourced.
 */
export const PromoPointsList = ({ className }: { className?: string }) => (
  <ul className={className ?? 'space-y-4'}>
    {PROMO_POINTS.map(({ icon: Icon, title, text }) => (
      <li key={title} className="flex gap-3">
        <div className="shrink-0 w-9 h-9 rounded-xl bg-white/15 backdrop-blur-md border border-white/20 flex items-center justify-center">
          <Icon className="w-5 h-5 text-white" />
        </div>
        <div>
          <p className="font-semibold text-white leading-snug">{title}</p>
          <p className="text-white/70 text-sm leading-snug">{text}</p>
        </div>
      </li>
    ))}
  </ul>
);

/**
 * `page` is the Projects header, `card` the smaller copy inside the About
 * panel. They differ only in logo size and heading scale, so they share one
 * component rather than two near-identical blocks that drift apart.
 */
const HERO_VARIANTS = {
  page: { logoSize: 128, headingClass: 'text-6xl' },
  card: { logoSize: 96, headingClass: 'text-5xl' },
} as const;

/**
 * Logo + app name + tagline.
 *
 * Shared with `ProjectsPageSkeleton` so the loading state reserves this block's
 * exact height (128px in the `page` variant — set by the logo, not the text).
 * If it lived only in `Projects.tsx` the skeleton would be guessing at it, and
 * the guess would rot the first time the copy or the logo size changed.
 *
 * `AuthPage` still renders its own copy of the `page` variant; it can adopt
 * this whenever that file is next touched.
 */
export const AppTitleHero = ({
  variant = 'page',
  as: Heading = 'h1',
  className = '',
}: {
  variant?: keyof typeof HERO_VARIANTS;
  /** Heading level — `h1` as a page title, `h3` inside a section. */
  as?: 'h1' | 'h3';
  className?: string;
}) => {
  const { logoSize, headingClass } = HERO_VARIANTS[variant];
  return (
    <div className={`flex items-center gap-8 ${className}`}>
      <Logo size={logoSize} className="shrink-0 [&_img]:shadow-[0_0_40px_8px_rgba(255,255,255,0.95)]" />
      <div>
        <Heading className={`${headingClass} font-extrabold text-white tracking-tight drop-shadow-lg`}>
          {APP_NAME}
        </Heading>
        <p className="text-white/90 text-xl mt-3">{PROMO_TAGLINE}</p>
      </div>
    </div>
  );
};

/**
 * The "About this app" section at the foot of the Projects page.
 *
 * Entirely static, so the loading skeleton renders the real thing rather than a
 * placeholder for it — nothing below it can shift when the project list lands.
 */
export const AboutThisAppSection = () => (
  <div className="mt-12">
    <h2 className="text-xl font-semibold text-white mb-4">About this app</h2>

    <div className="bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl p-6 sm:p-10">
      {/* Logo + app name + tagline (mirrors the login hero) */}
      <AppTitleHero variant="card" as="h3" className="mb-10" />

      <h4 className="text-3xl font-extrabold text-white leading-tight drop-shadow-lg mb-7">
        {PROMO_HEADLINE}
      </h4>
      <PromoPointsList className="space-y-4" />
    </div>
  </div>
);
